import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Briefcase, Heart, Music, Globe, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >{children}</motion.div>
  );
}

export default function Careers() {
  const values = [
    { icon: <Music size={24} />, title: 'Music First', desc: 'Everything we do starts and ends with the music. If you live and breathe lofi, you belong here.' },
    { icon: <Heart size={24} />, title: 'Passion-Driven', desc: 'We are a small team fueled by genuine love for what we do. No corporate bureaucracy.' },
    { icon: <Globe size={24} />, title: 'Fully Remote', desc: 'Work from anywhere in the world. We believe great work happens when you are comfortable.' },
  ];

  const openings = [
    { title: 'A&R Scout', type: 'Part-time · Remote', desc: 'Find and evaluate new lofi talent across Spotify, SoundCloud, and Bandcamp. Help shape the CloudfiMusic roster.' },
    { title: 'Social Media Manager', type: 'Part-time · Remote', desc: 'Manage our Instagram, TikTok, and Twitter presence. Create engaging content that resonates with the lofi community.' },
    { title: 'Playlist Curator', type: 'Freelance · Remote', desc: 'Curate and maintain official CloudfiMusic playlists. Deep knowledge of lofi, ambient, and chill subgenres required.' },
    { title: 'Visual Artist / Designer', type: 'Freelance · Remote', desc: 'Create album artwork, social media visuals, and brand assets. Must have a strong aesthetic aligned with lofi culture.' },
  ];

  return (
    <div className="career-page">
      <div className="career-hero">
        <motion.div className="career-hero-badge"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
        ><Briefcase size={16} /> We're Hiring</motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >Join the <span>Team</span></motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >Help us shape the future of independent lofi music. We are always looking for passionate people to join CloudfiMusic.</motion.p>
      </div>

      {/* Values */}
      <div className="career-values">
        {values.map((v, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="career-value">
              <div className="career-value-icon">{v.icon}</div>
              <h3>{v.title}</h3>
              <p>{v.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Openings */}
      <div className="career-openings">
        <Reveal><h2>Open <span>Positions</span></h2></Reveal>
        <div className="career-list">
          {openings.map((o, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <a href={`mailto:info@cloudfimusic.com?subject=Application: ${o.title}`} className="career-card">
                <div className="career-card-info">
                  <h3>{o.title}</h3>
                  <span className="career-card-type">{o.type}</span>
                  <p>{o.desc}</p>
                </div>
                <ArrowRight size={20} className="career-card-arrow" />
              </a>
            </Reveal>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="career-cta">
        <Reveal>
          <h2>Don't see your role?</h2>
          <p>We're always open to hearing from talented individuals. Send us your portfolio and tell us how you can contribute.</p>
          <a href="mailto:info@cloudfimusic.com?subject=General Application" className="btn btn-primary">
            Send Application <Mail size={16} />
          </a>
        </Reveal>
      </div>
    </div>
  );
}
